import React, { useCallback, useEffect, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { StatusManager } from 'common/components/StatusManager';
import useOnScreen from 'hooks/useOnScreen';
import qs from 'query-string';
import API from 'services/API';
import strings from 'strings';
import Text from 'UI/components/atoms/Text';
import HistoryItem from 'UI/components/molecules/HistoryItem';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { DateFormats } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { UIStatus } from 'UI/constants/status';
import { getId } from 'UI/utils';

import { useStyles } from './styles';

const firstPage = 1;

const ActivityLogDrawer = ({ open, onClose, searchProjectId }) => {
  const classes = useStyles();
  const [loaderRef, isLoaderVisible] = useOnScreen();

  const [activityLogs, setActivityLogs] = useState({
    logs: [],
    pagination: {
      page: 1,
      perPage: 20,
      limitReached: false
    }
  });
  const [uiState, setUiState] = useState({
    lastScrollTop: 0,
    isFormLoading: true
  });

  const getActivities = useCallback(
    async page => {
      const endpoint = qs.stringifyUrl({
        url: Endpoints.SearchProjectHistoryEvents.replace(/:id/, searchProjectId),
        query: {
          page,
          perPage: activityLogs.pagination.perPage
        }
      });
      const response = await API.get(endpoint);
      if (response.status === 200) {
        const { data } = response;
        const formattedData = data.map(log => ({ ...log, id: getId() }));
        setActivityLogs(prev => ({
          logs: [...prev.logs, ...formattedData],
          pagination: {
            ...prev.pagination,
            page: prev.pagination.page + 1,
            limitReached: data.length < 20
          }
        }));
      }
    },
    [activityLogs.pagination.perPage, searchProjectId]
  );

  useEffect(() => {
    (async () => {
      setUiState(prev => ({ ...prev, isFormLoading: true }));
      await getActivities(firstPage);
      setUiState(prev => ({ ...prev, isFormLoading: false }));
    })();
  }, [getActivities]);

  useEffect(() => {
    if (isLoaderVisible) {
      getActivities(activityLogs.pagination.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaderVisible]);

  return (
    <FPDrawer
      CardHeaderProps={{ title: strings.searchProjects.activityLog.drawer.title }}
      FPDrawerProps={{
        open,
        onClose
      }}
      onClose={onClose}
    >
      <StatusManager status={uiState.isFormLoading ? UIStatus.Loading : UIStatus.Success}>
        <div className={classes.root}>
          <Typography variant="subtitle1" paragraph>
            {strings.searchProjects.activityLog.drawer.content}
          </Typography>

          <Grid container direction="column" justify="center">
            {activityLogs.logs.map(log => (
              <HistoryItem
                key={log.id}
                action={log.description}
                date={log.date}
                dateFormat={DateFormats.SimpleDateTime}
                type="custom"
              />
            ))}

            {!activityLogs.pagination.limitReached &&
              !uiState.isFormLoading &&
              activityLogs.logs.length > 0 && (
                <CircularProgress ref={loaderRef} size={24} className={classes.loader} />
              )}
            {activityLogs.pagination.limitReached && (
              <Text variant="caption" text="No more results to show" />
            )}
          </Grid>
        </div>
      </StatusManager>
    </FPDrawer>
  );
};

export default ActivityLogDrawer;
